import { useEffect, useRef, useState } from "react";

/**
 * Custom hook that manages a cooldown period after a trigger value transitions from true to false.
 * Useful for preventing rapid repeated actions (e.g., API calls).
 *
 * @param trigger - A boolean value that triggers the cooldown when it transitions from true to false
 * @param duration - The cooldown duration in milliseconds (default: 15000ms)
 * @returns A boolean indicating whether the cooldown is currently active
 */
export const useCooldown = (
  trigger: boolean,
  duration: number = 15000,
): boolean => {
  const [isCooldown, setIsCooldown] = useState(false);
  const prevTriggerRef = useRef(trigger);

  useEffect(() => {
    if (prevTriggerRef.current && !trigger) {
      setIsCooldown(true);
    }
    prevTriggerRef.current = trigger;
  }, [trigger]);

  useEffect(() => {
    if (isCooldown) {
      const timeout = setTimeout(() => {
        setIsCooldown(false);
      }, duration);

      return () => clearTimeout(timeout);
    }
  }, [isCooldown, duration]);

  return isCooldown;
};
